import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { Form } from "@circle-react-uikit/Form";
import { CustomCoverImagePicker } from "./CustomCoverImagePicker";
import { MediaPicker } from "./MediaPicker";
import type { InlineAttachment } from "./MediaPicker/useMediaPicker";
import { VideoSettings } from "./VideoSettings";
import { SOURCE_OPTIONS } from "./constants";
import { useVideoUploader } from "./useVideoUploader";
import { useVideoUploaderFields } from "./useVideoUploaderFields";

export interface VideoUploaderProps {
  name: string;
}

export interface VideoUploaderFormFields {
  [name: string]: {
    url: string;
    source: string;
    signed_id: string;
    customCoverImage: InlineAttachment | null;
    html: string;
    hasCustomCoverImage: boolean;
    isVideoMuted: boolean;
    isVideoLooped: boolean;
    hasAltText: boolean;
    videoAltText: string;
    customCoverImageSignedId: string;
  };
}

export const VideoUploader = ({ name }: VideoUploaderProps) => {
  const {
    signed_id,
    customCoverImageUrl,
    url,
    filename,
    createEmbedMutation,
    handleOnUploadStarted,
    handleOnUploadComplete,
  } = useVideoUploader({ name });

  const { source, handleOnChangeUrl, handleOnClickRemove } =
    useVideoUploaderFields({
      name,
      createEmbedMutation,
    });

  return (
    <div className="flex flex-col gap-5">
      <Form.Item
        name={`${name}.source`}
        label={t("builder_ui.prop_type.video_upload.source")}
        hideDescription
        hideBorder
        labelClassName="!font-normal text-xs"
        className="!flex !items-center !p-0"
      >
        <Form.SelectNative name={`${name}.source`} options={SOURCE_OPTIONS} />
      </Form.Item>

      <div className={classNames({ hidden: source !== "url" })}>
        <Form.Item
          name={`${name}.url`}
          label={t("builder_ui.prop_type.video_upload.paste_url")}
          hideDescription
          hideBorder
          labelClassName="!font-normal text-xs"
          className="!p-0"
          placeholder={t("builder_ui.prop_type.video_upload.url_placeholder")}
          isDisabled={createEmbedMutation.isLoading}
        >
          <Form.Input
            name={`${name}.url`}
            onChange={handleOnChangeUrl}
            disabled={createEmbedMutation.isLoading}
          />
        </Form.Item>
      </div>

      <div
        className={classNames("flex flex-col gap-5", {
          hidden: source !== "upload",
        })}
      >
        <MediaPicker
          onUploadStarted={handleOnUploadStarted}
          onUploadComplete={handleOnUploadComplete}
          onClickRemove={handleOnClickRemove}
          file={{
            signed_id,
            url,
            filename,
            thumbnail_url: customCoverImageUrl,
          }}
        />
        <CustomCoverImagePicker name={name} />
        <VideoSettings name={name} />
      </div>
    </div>
  );
};
